import CupIcon from "@/assets/cup.svg";
import TournamentIcon from "@/assets/tournaments.svg";
import { useQuery } from "@tanstack/react-query";
import { parseAsString, parseAsStringEnum, useQueryState } from "nuqs";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { FavoritesListSkeleton } from "@/components/skeleton/list-skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RootField, TextField } from "@/components/ui/text-field";
import { useBreakpoints } from "@/hooks/use-breakpoints";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { useTRPC } from "@/lib/trpc";
import { routes } from "@/routes";

import { ClubListItem } from "../club-list/club-list-item";
import { TournamentListItem } from "../tournament-list/tournament-list-item";

type ClubType = RouterOutputs["player"]["getPlayerFavoriteClubs"][number];
type TournamentType = RouterOutputs["player"]["getPlayerFavoriteTournament"][number];

export function PlayerFavorites() {
  const trpc = useTRPC();
  const { data, status } = useQuery(trpc.player.getPlayerFavoriteClubs.queryOptions());
  const { data: tournaments, status: tournamentsStatus } = useQuery(trpc.player.getPlayerFavoriteTournament.queryOptions());
  const { getCountryName } = useTranslationUtils();
  const [search, setSearch] = useQueryState("search", parseAsString.withDefault(""));
  const { t } = useTranslation(["common", "profile", "tournaments"]);
  const breakpoint = useBreakpoints();
  const [activeTab, setActiveTab] = useQueryState("tab", parseAsStringEnum(["clubs", "tournaments"]).withDefault("clubs"));

  if (status === "pending" || tournamentsStatus === "pending") {
    return <FavoritesListSkeleton />;
  }
  if (status === "error" || tournamentsStatus === "error") {
    return null;
  }

  const filterBySearchClub = (club: ClubType, search: string) => {
    const searchTerm = search.toLowerCase();
    return (
      club.clubName.toLowerCase().includes(searchTerm)
      || club.clubCity.toLowerCase().includes(searchTerm)
      || getCountryName(club.clubCountryCode).toLowerCase().includes(searchTerm)
    );
  };
  const filterBySearchTournament = (tournament: TournamentType, search: string) => {
    const searchTerm = search.toLowerCase();
    return (
      tournament.clubName?.toLowerCase().includes(searchTerm)
      || tournament.name?.toLowerCase().includes(searchTerm)
      || tournament.city.toLowerCase().includes(searchTerm)
      || getCountryName(tournament.country).toLowerCase().includes(searchTerm)
    );
  };

  const filteredFavoritesClubs = data.filter(club =>
    filterBySearchClub(club, search),
  );
  const filteredFavoritesTournaments = tournaments.filter(tournament =>
    filterBySearchTournament(tournament, search),
  );

  return (
    <Tabs defaultValue={activeTab} asChild>
      <div className="container flex flex-col gap-4 md:p-4">
        <div className="grid grid-cols-1 items-center gap-y-2 bg-neutral-700 p-3 sm:grid-cols-2 md:rounded-xl md:px-4 md:py-5 lg:grid-cols-4 lg:gap-x-4">
          <h1 className="order-1 col-span-1 shrink-0 text-nowrap text-center text-lg font-medium text-white sm:text-start md:text-start md:text-xl">
            {t("common:favorites-list")}
          </h1>

          <RootField className="order-2 col-span-2 flex-1 sm:order-3 lg:order-2">
            <TextField
              clearable
              onClear={() => {
                setSearch("");
              }}
              value={search}
              placeholder={t("common:search-fav")}
              className="col-span-2 flex-1 border-0 text-white"
              onChange={event => setSearch(event.target.value)}
            />
          </RootField>

          <TabsList className="order-3 col-span-1 flex h-12 w-full rounded-lg bg-neutral-500 p-0.5 sm:order-2 sm:w-fit sm:justify-self-end lg:order-3">
            <TabsTrigger
              value="clubs"
              onClick={() => setActiveTab("clubs")}
              className="flex size-full items-center gap-1.5 rounded-md border-0 px-2 text-white data-[state=active]:border-0 data-[state=active]:bg-neutral-700 data-[state=active]:text-white"
            >
              <CupIcon className="size-5 shrink-0 text-orange-500" />
              <span className="text-sm font-medium">Clubs</span>
            </TabsTrigger>
            <TabsTrigger
              value="tournaments"
              onClick={() => setActiveTab("tournaments")}
              className="flex size-full items-center gap-1.5 rounded-md border-0 px-2 text-white data-[state=active]:border-0 data-[state=active]:bg-neutral-700 data-[state=active]:text-white"
            >
              <TournamentIcon className="size-5 shrink-0 text-orange-500" />
              <span className="text-sm font-medium">{t("common:desktop-nav.tournaments")}</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="clubs" className="h-full px-2 pb-4 md:px-0">
          <div className="flex flex-col gap-4 md:gap-0 md:rounded-[18px] md:border md:border-neutral-400">
            {filteredFavoritesClubs.length > 0 && (breakpoint === "lg" || breakpoint === "xl") && (
              <div
                className="hidden grid-cols-[295px_52px_160px_298px_116px] items-center gap-3 rounded-t-[18px] bg-neutral-50 px-2.5 py-2 text-xs font-medium text-neutral-500 lg:grid"
              >
                <span className="p-2">
                  Clubs (
                  {filteredFavoritesClubs.length}
                  )
                </span>

                <span className="p-2 text-center">
                  {t("common:country")}
                </span>

                <span className="p-2 text-center">
                  {t("common:teams")}
                </span>

                <span className="p-2 text-center">
                  {`${t("profile:players-wanted")} ${t("common:men").charAt(0)}/${t("common:women").charAt(0)}`}
                </span>

                <span className="p-2 text-center">
                  Actions
                </span>
              </div>
            )}

            {
              filteredFavoritesClubs.length
                ? filteredFavoritesClubs.map(club => (
                    <ClubListItem key={club.id} club={club} fromPath={routes.favorites} />
                  ))
                : (
                    <span className="rounded-[18px] border border-neutral-400 bg-white p-4 text-center text-neutral-600 md:border-0">
                      {t("common:no-club-found")}
                    </span>
                  )
            }
          </div>
        </TabsContent>

        <TabsContent value="tournaments" className="h-full px-2 pb-4 md:px-0">
          <div className="flex flex-col gap-4 md:gap-0 md:rounded-[18px] md:border md:border-neutral-400 lg:border">
            { filteredFavoritesTournaments.length > 0
            && (breakpoint === "lg" || breakpoint === "xl") && (
              <div
                className="hidden grid-cols-[300px,55px,80px,140px,auto,120px] items-center gap-2.5 rounded-t-[18px] bg-neutral-50 px-2.5 py-2 text-xs font-medium text-neutral-500 lg:grid"
              >
                <span className="py-2 pl-2">
                  {`${t("common:desktop-nav.tournaments")}  (${tournaments.length})`}
                </span>

                <span className="py-2 text-center">
                  {t("common:country")}
                </span>

                <span className="text-nowrap py-2 text-center">
                  {t("tournaments:winner-prize")}
                </span>

                <span className="py-2 text-center">
                  {t("profile:surface")}
                </span>

                <div className="flex items-center gap-4">
                  <div className="flex w-full justify-center text-nowrap ">
                    <span>{t("tournaments:tournament-start-date")}</span>
                  </div>

                  <div className="flex w-full justify-center text-nowrap ">
                    <span>{t("tournaments:tournament-end-date")}</span>
                  </div>
                </div>

                <span className="py-2 text-center">
                  Actions
                </span>
              </div>
            )}

            { filteredFavoritesTournaments.length
              ? filteredFavoritesTournaments.map(tournament => (
                  <TournamentListItem tournament={tournament} key={tournament.id} fromPath={routes.favorites} />
                ))
              : (
                  <span
                    className="rounded-[18px] border border-neutral-400 bg-white p-4 text-center text-neutral-600 md:border-0"
                  >
                    {t("tournaments:empty-tournament-list")}
                  </span>
                )}
          </div>
        </TabsContent>
      </div>
    </Tabs>
  );
}
